<script setup lang="ts">
import { computed } from 'vue'
import moment from 'moment'
import { HSDropdown } from 'preline'
import MessageIcon from '@/assets/images/ide/message.svg?component'
import MessageStarIcon from '@/assets/images/ide/message-star.svg?component'
import deleteIcon from '@/assets/images/ide/trash.svg'
import renameIcon from '@/assets/images/ide/pencil.svg'
import advanceCompilerDatabaseService, {
  type CodeQueryDto
} from '@/services/ide/advanceCompilerDatabase.service'
import editorService from '@/services/ide/editor.service'
import utilModelsService from '@/services/util.models.service'
import { useIdeStore } from '@/stores/ide.store'
import { useApiStore } from '@/stores/api.store'
import { useAdvanceCompilerDatabaseStore } from '@/stores/advanceCompilerDatabase.store'
import { IDECONSTANT } from '@/utils/ide'
import BaseInput from '@/components/forms/BaseInput.vue'
import tabsSettingIcon from '@/assets/images/ide/dots-vertical.svg'

const ideStore = useIdeStore()
const apiStore = useApiStore()
const advCompDbStore = useAdvanceCompilerDatabaseStore()

const DBInstanceName = computed(() => {
  return advCompDbStore.databaseInstanceName
})

/**
 * @param query details of selected query from logs
 */
const handleClickQuery = async (query: CodeQueryDto) => {
  if (advCompDbStore.isCodeExecutingDb) return

  advanceCompilerDatabaseService
    .queryDetail(query.id)
    .then((response) => {
      ideStore.setUniqueActiveItemAdvIde({
        name: query.fileName,
        id: query.id,
        content: response.data?.script
      })
      ideStore.setActiveFileDataBase({ id: query.id })
      editorService.setEditorSession(IDECONSTANT.CODE_EDITOR, response.data?.script)
    })
    .catch(() => {
      apiStore.setGlobalMsg(`Fail to load query ${query.fileName}!`)
    })
}

/**
 * @param query details of selected query from logs
 */
const handleSubmitRenameFile = async (query: CodeQueryDto) => {
  handleCloseEditMode(query, true)

  advanceCompilerDatabaseService
    .updateQueryLogs(query)
    .then(() => {
      ideStore.setUniqueActiveItemAdvIde({
        name: query.fileName,
        id: query.id,
        preventUnshift: true
      })
    })
    .catch(() => {
      advCompDbStore.queryLogs.find((que) => {
        if (que.id === query.id) {
          que.fileName = que.renamedFileName as string
        }
      })

      ideStore.setUniqueActiveItemAdvIde({
        name: query.renamedFileName,
        id: query.id,
        preventUnshift: true
      })
    })
    .finally(() => {
      advCompDbStore.queryLogs.find((que) => {
        if (que.id === query.id) {
          que.renamedFileName = ''
        }
      })
    })
}

/**
 * @param query details of selected query from logs
 * @param rename to update renamed file name to input
 */
const handleCloseEditMode = (query: CodeQueryDto, rename: boolean = false) => {
  advCompDbStore.queryLogs.find((que) => {
    if (que.id === query.id) {
      que.isRename = false
      const originalFileName = que.fileName
      if (rename) {
        que.fileName = que.renamedFileName as string
        que.renamedFileName = originalFileName
      }
    }
  })
}

/**
 * @param query details of selected query from logs
 */
const handleClickRename = async (query: CodeQueryDto) => {
  advCompDbStore.queryLogs.find((que) => {
    if (que.id === query.id) {
      que.isRename = true
      que.renamedFileName = que.fileName
    }
  })

  HSDropdown.close(`#query-action-dropdown-${query.id}`)

  await new Promise((resolve) => setTimeout(resolve, 50))
  const input = document.getElementById(`query-name-${query.id}`) as HTMLInputElement
  input.value = query.fileName
  input.focus()
}

/**
 * @param query details of selected query from logs
 */
const handleClickDelete = async (query: CodeQueryDto) => {
  await utilModelsService
    .confirmPromise('Are you sure want to delete this query?')
    .then(async () => {
      await advanceCompilerDatabaseService
        .deleteQuery(query.id)
        .then(() => {
          advanceCompilerDatabaseService.handleNextOrPrevActiveTab(query)

          const filteredTabs = ideStore.uniqueActiveItemAdvIde?.filter(
            (item) => item?.id != query?.id
          )
          ideStore.uniqueActiveItemAdvIde = filteredTabs

          const filteredQueries = advCompDbStore.queryLogs.filter((que) => que?.id !== query.id)
          advCompDbStore.setQueryLogs(filteredQueries)
        })
        .catch(() => {
          apiStore.setGlobalMsg(`Failed to delete ${query.fileName}!`)
        })
    })
}

/**
 * @param query details of selected query from logs
 * @returns is query selected in tab bar or not
 */
const isSelected = (query: CodeQueryDto) => {
  return ideStore.activeItem?.id === query?.id
}
</script>

<template>
  <aside
    class="stroke-primary-default background-primary flex h-full w-full flex-col border-r border-t"
  >
    <header class="flex items-center gap-2 px-3 pt-2">
      <MessageIcon class="icon-ide-emphasized h-4 w-4" />
      <h2 class="text-xs font-medium">Query logs</h2>
    </header>
    <section v-if="!advCompDbStore.isFetchingQueryLogs" class="flex-grow overflow-y-scroll">
      <ul v-if="advCompDbStore.queryLogs?.length !== 0" class="space-y-2 pl-3 pt-2">
        <li
          v-for="query in advCompDbStore.queryLogs"
          :key="query.lastUpdate"
          class="hover:surface-primary-base flex items-start gap-2 rounded px-3 py-2"
          :class="[
            advCompDbStore.isCodeExecutingDb ? 'cursor-not-allowed' : 'cursor-pointer',
            {
              'surface-primary-base bg-opacity-60': isSelected(query)
            }
          ]"
          @click="handleClickQuery(query)"
        >
          <MessageStarIcon class="icon-ide-default mt-0.5 h-4 w-4 shrink-0" />
          <article class="min-w-0 flex-1 text-sm">
            <form
              v-if="query.isRename"
              @submit.prevent="handleSubmitRenameFile(query)"
              @click.stop
              class="flex w-full items-center gap-1"
            >
              <BaseInput
                :id="`query-name-${query.id}`"
                :showLable="false"
                v-model="query.renamedFileName"
                class="!my-0 h-6 max-w-24 rounded-md py-0"
                :isContactus="true"
                required
                maxlength="25"
              />
              <button type="submit" class="flex">
                <FontAwesomeIcon icon="fa-circle-check" :class="['h-4', 'w-4', 'success']" />
              </button>
              <button @click="handleCloseEditMode(query)" class="flex">
                <FontAwesomeIcon icon="fa-circle-xmark" class="error h-4 w-4" />
              </button>
            </form>
            <h3
              :title="query.fileName"
              v-else
              class="text-mid my-0.5 max-w-32 truncate text-sm font-light"
            >
              {{ query.fileName }}
            </h3>

            <p class="text-high font-medium">{{ query.database ?? 'No database' }}</p>
            <time class="text-low font-light" :datetime="query.lastUpdate">
              {{ moment.utc(query.lastUpdate).fromNow() }}
            </time>
          </article>

          <div
            :id="`query-action-dropdown-${query.id}`"
            class="hs-dropdown relative ml-auto flex shrink-0 justify-end [--placement:bottom-right]"
          >
            <img
              :src="tabsSettingIcon"
              class="hs-dropdown-toggle h-4 w-4"
              :class="advCompDbStore.isCodeExecutingDb ? 'pointer-events-none' : ''"
              :id="`hs-dropdown-edit-query-log-${query.id}`"
              @click.stop
            />

            <div
              class="hs-dropdown-menu duration background-primary z-10 mt-2 hidden min-w-10 rounded-lg opacity-0 shadow-md transition-[opacity,margin] before:absolute before:-top-4 before:start-0 before:h-4 before:w-full after:absolute after:-bottom-4 after:start-0 after:h-4 after:w-full hs-dropdown-open:opacity-100"
              role="menu"
              :aria-labelledby="`hs-dropdown-edit-query-log-${query.id}`"
            >
              <ul class="space-y-0.5 p-1">
                <li
                  class="text-mid hover:surface-secondary-base flex items-center gap-x-3.5 rounded p-2 text-sm"
                  @click.stop="handleClickRename(query)"
                >
                  <img :src="renameIcon" class="h-5 w-5" /> Rename
                </li>
                <li
                  class="text-mid hover:surface-secondary-base flex items-center gap-x-3.5 rounded p-2 text-sm"
                  @click.stop="handleClickDelete(query)"
                >
                  <img :src="deleteIcon" class="h-5 w-5" /> Delete
                </li>
              </ul>
            </div>
          </div>
        </li>
      </ul>
      <div v-else class="flex h-full w-full flex-col justify-center gap-2 p-2">
        <div class="text-mid flex justify-center text-xs font-medium leading-tight">
          No Query logs
        </div>
      </div>
    </section>
    <div
      v-else
      class="flex w-full flex-col justify-between gap-2"
      :class="DBInstanceName ? 'p-8' : 'p-4'"
    >
      <div
        class="text-high flex text-xs font-medium leading-tight"
        :class="DBInstanceName ? ' justify-start' : 'justify-center'"
      >
        Loading Info...
      </div>
    </div>
  </aside>
</template>
